/**
 * Outreach Activities API client.
 *
 * Wraps /api/outreach-activities — anonymous, aggregate-only outreach
 * form submissions for CBOs running community outreach programs
 * (Somos Mayfair / FUERTES, Latinas Contra Cancer, etc.).
 *
 * The backend (app/routers/outreach_activities.py) enforces:
 * - PII guard on every text value (rejects 422 with the disclosure copy)
 * - Anonymous-by-construction (all parent FKs forced NULL)
 * - Promotora row-level scoping (staff/viewer-tier users see only their
 *   own entries; supervisor+ sees all in the CBO)
 *
 * Phase 2b additions:
 * - GET /api/outreach-activities/export → text/csv stream (server-side)
 * - GET /api/calendar/attendance-report → per-promotora attendance rows
 *   (supervisor+ only; 403 for frontline-staff)
 *
 * All calls go through authFetch — which sets Authorization + X-CBO-ID.
 */

import { authFetchJson, authFetch } from '@/lib/authFetch';

export interface OutreachActivity {
  id: number;
  cbo_id: number;
  form_template_id: number;
  form_template_name: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  field_data: Record<string, any>;
  notes?: string | null;
  assigned_to_user_id?: number | null;
  created_ts: string;
  last_updated_ts: string;
  submitted_at?: string | null;
}

export interface OutreachActivityCreate {
  form_template_id: number;
  field_data: Record<string, any>;
  status?: 'draft' | 'submitted';
  notes?: string;
}

export interface ListOutreachActivitiesParams {
  date_from?: string; // ISO date
  date_to?: string;
  activity_type?: string;
  engagement_type?: string;
  promotora_id?: number;
  status?: string;
  limit?: number;
  offset?: number;
}

function buildQuery(params: Record<string, any> = {}): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export async function listOutreachActivities(
  params: ListOutreachActivitiesParams = {},
): Promise<OutreachActivity[]> {
  return authFetchJson<OutreachActivity[]>(`/api/outreach-activities/${buildQuery(params)}`);
}

export async function getOutreachActivity(id: number): Promise<OutreachActivity> {
  return authFetchJson<OutreachActivity>(`/api/outreach-activities/${id}`);
}

export interface CreateOutreachActivityResult {
  ok: true;
  activity: OutreachActivity;
}
export interface PiiRejection {
  ok: false;
  kind: 'pii';
  field: string;
  category: string; // "email address" | "phone number"
  message: string;
}
export interface OtherCreateError {
  ok: false;
  kind: 'other';
  message: string;
  status: number;
}

/**
 * Stream the outreach activity list as text/csv. Browser handles the
 * download via Content-Disposition. Same filter surface as
 * listOutreachActivities; honors the same server-side Promotora scoping.
 */
export async function downloadOutreachCsv(params: ListOutreachActivitiesParams = {}): Promise<void> {
  const res = await authFetch(`/api/outreach-activities/export${buildQuery(params)}`);
  if (!res.ok) {
    throw new Error((await res.text()) || `Export failed (${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const stamp = new Date().toISOString().slice(0, 10);
  a.download = `outreach-activities_${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Attendance report (Phase 2b backend endpoint)
// ---------------------------------------------------------------------------

export interface AttendanceReportRow {
  event_id: number;
  event_title: string;
  event_type: 'network_hub_meeting' | 'training_workshop';
  event_date: string | null;
  duration_minutes: number | null;
  user_id: number;
  promotora_name: string | null;
  promotora_email: string | null;
  response_status: string | null;
}

export interface AttendanceReportParams {
  start_date?: string;
  end_date?: string;
  event_type?: 'network_hub_meeting' | 'training_workshop';
  promotora_id?: number;
}

export interface AttendanceReportResponse {
  rows: AttendanceReportRow[];
  total: number;
}

export async function getAttendanceReport(
  params: AttendanceReportParams = {},
): Promise<AttendanceReportResponse> {
  return authFetchJson<AttendanceReportResponse>(
    `/api/calendar/attendance-report${buildQuery({ ...params, format: 'json' })}`,
  );
}

/**
 * Stream the attendance report as text/csv (server-side rendering — same
 * shape as the JSON response, flat columns).
 */
export async function downloadAttendanceCsv(params: AttendanceReportParams = {}): Promise<void> {
  const res = await authFetch(
    `/api/calendar/attendance-report${buildQuery({ ...params, format: 'csv' })}`,
  );
  if (!res.ok) {
    throw new Error((await res.text()) || `Export failed (${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const stamp = new Date().toISOString().slice(0, 10);
  a.download = `attendance-report_${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Manager approval gate (Phase 5)
// ---------------------------------------------------------------------------

export interface ReviewDecisionPayload {
  notes?: string;
}

export interface ReviewError {
  ok: false;
  status: number;
  message: string;
}
export interface ReviewSuccess {
  ok: true;
  activity: OutreachActivity;
}

async function _review(
  activityId: number,
  action: 'approve' | 'reject',
  payload: ReviewDecisionPayload,
): Promise<ReviewSuccess | ReviewError> {
  const res = await authFetch(`/api/outreach-activities/${activityId}/${action}`, {
    method: 'POST',
    jsonBody: payload,
  });
  if (res.ok) {
    return { ok: true, activity: (await res.json()) as OutreachActivity };
  }
  let detail: any = null;
  try {
    detail = await res.json();
  } catch {
    // ignored
  }
  return {
    ok: false,
    status: res.status,
    message: typeof detail?.detail === 'string' ? detail.detail : detail?.message || res.statusText,
  };
}

export const approveOutreachActivity = (id: number, payload: ReviewDecisionPayload = {}) =>
  _review(id, 'approve', payload);

export const rejectOutreachActivity = (id: number, payload: ReviewDecisionPayload = {}) =>
  _review(id, 'reject', payload);

/**
 * Create a new outreach activity. Returns a discriminated union so callers
 * can branch on the PII-rejection case without parsing the error themselves.
 */
export async function createOutreachActivity(
  payload: OutreachActivityCreate,
): Promise<CreateOutreachActivityResult | PiiRejection | OtherCreateError> {
  const res = await authFetch('/api/outreach-activities/', {
    method: 'POST',
    jsonBody: payload,
  });
  if (res.ok) {
    const activity = (await res.json()) as OutreachActivity;
    return { ok: true, activity };
  }
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    // ignored — handled below
  }
  if (res.status === 422 && body?.detail?.error === 'pii_in_outreach_record') {
    return {
      ok: false,
      kind: 'pii',
      field: body.detail.field,
      category: body.detail.kind,
      message: body.detail.message,
    };
  }
  return {
    ok: false,
    kind: 'other',
    message: body?.detail?.message || body?.detail || res.statusText,
    status: res.status,
  };
}
